import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@paulownia/shared/components/ui/dialog';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@paulownia/shared/components/ui/popover';
import { NumericKeypad } from '@paulownia/shared/components/NumericKeypad';
import { TimePicker } from '@paulownia/shared/components/TimePicker';
import { ProductSelectionModal } from '@paulownia/shared/components/ProductSelectionModal';
import { Badge } from '@paulownia/shared/components/ui/badge';
import { Trash2 } from 'lucide-react';
import { Order, Product, OrderLineOption, derivePickupDate, calculateOrderTotal, getOptionLabels } from '@paulownia/shared/types';
import { updateOrder, updateOrderStatus } from '@paulownia/shared/services/orders';
import { toast } from 'sonner';
import { useProductLookup } from '@paulownia/shared/hooks/useProductLookup';

interface LineForm {
    productId: string;
    productName: string;
    quantity: number;
    preparedQuantity: number;
    options: OrderLineOption[];
}

interface TenantSettings {
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
}

interface OrderEditDialogProps {
    order: Order | null;
    isOpen: boolean;
    onClose: () => void;
    products: Product[];
    tenantSettings?: TenantSettings | null;
}

export function OrderEditDialog({ order, isOpen, onClose, products, tenantSettings }: OrderEditDialogProps) {
    // Use the optimized product lookup hook
    const { getName: getProductName, getSaleMode: getProductSaleMode } = useProductLookup(products);

    const [editCustomerName, setEditCustomerName] = useState('');
    const [editPickupDate, setEditPickupDate] = useState('');
    const [editPickupTime, setEditPickupTime] = useState('');
    const [editNote, setEditNote] = useState('');
    const [editLines, setEditLines] = useState<LineForm[]>([]);
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [editLinePopoverId, setEditLinePopoverId] = useState<number | null>(null);

    useEffect(() => {
        if (order && isOpen) {
            setEditCustomerName(order.customerName);
            setEditNote(order.note || '');
            const pickupDate = new Date(order.pickupAtISO);
            const year = pickupDate.getFullYear();
            const month = String(pickupDate.getMonth() + 1).padStart(2, '0');
            const day = String(pickupDate.getDate()).padStart(2, '0');
            setEditPickupDate(`${year}-${month}-${day}`);
            setEditPickupTime(`${pickupDate.getHours().toString().padStart(2, '0')}:${pickupDate.getMinutes().toString().padStart(2, '0')}`);

            setEditLines(order.lines.map(l => ({
                productId: l.productId,
                productName: getProductName(l.productId),
                quantity: l.quantity,
                preparedQuantity: l.preparedQuantity,
                options: l.options || [],
            })));
        }
    }, [order, isOpen, products, getProductName]);

    // Helper to compare options arrays
    const optionsEqual = (opts1: OrderLineOption[], opts2: OrderLineOption[]): boolean => {
        if (opts1.length !== opts2.length) return false;
        const sorted1 = [...opts1].sort((a, b) => a.id.localeCompare(b.id));
        const sorted2 = [...opts2].sort((a, b) => a.id.localeCompare(b.id));
        return sorted1.every((opt, i) => {
            if (opt.id !== sorted2[i].id) return false;
            const val1 = opt.value;
            const val2 = sorted2[i].value;
            // Handle array comparison for multiselect
            if (Array.isArray(val1) && Array.isArray(val2)) {
                if (val1.length !== val2.length) return false;
                const sorted1Arr = [...val1].sort();
                const sorted2Arr = [...val2].sort();
                return sorted1Arr.every((v, j) => v === sorted2Arr[j]);
            }
            return val1 === val2;
        });
    };

    function handleAddProductToEdit(selection: {
        productId: string;
        productName: string;
        quantity: number;
        options: OrderLineOption[];
    }) {
        setEditLines(prev => {
            const existingIndex = prev.findIndex(
                (line) =>
                    line.productId === selection.productId &&
                    optionsEqual(line.options, selection.options)
            );
            if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    quantity: updated[existingIndex].quantity + selection.quantity,
                };
                return updated;
            }
            return [...prev, { ...selection, preparedQuantity: 0 }];
        });
    }

    function handleRemoveLineFromEdit(index: number) {
        setEditLines(prev => prev.filter((_, i) => i !== index));
    }

    function handleUpdateLineQuantity(index: number, newQuantity: number) {
        setEditLines(prev => prev.map((line, i) => {
            if (i === index) {
                return {
                    ...line,
                    quantity: Math.max(0, Math.round(newQuantity * 1000) / 1000),
                };
            }
            return line;
        }));
    }

    async function handleSaveOrder() {
        if (!order) return;
        try {
            if (!editCustomerName.trim()) throw new Error('Nom client requis');
            if (!editPickupDate) throw new Error('Date de récupération requise');
            if (!editPickupTime || !editPickupTime.includes(':')) throw new Error('Veuillez sélectionner une heure de récupération');
            if (editLines.length === 0) throw new Error('Ajoutez au moins un produit');

            const [h, m] = editPickupTime.split(':').map((n) => Number(n));
            const [year, month, day] = editPickupDate.split('-').map((n) => Number(n));
            const at = new Date(year, month - 1, day, h, m, 0, 0);

            const updatedLines = editLines.map(l => ({
                productId: l.productId,
                quantity: l.quantity,
                preparedQuantity: l.preparedQuantity,
                options: l.options,
            }));

            const newPickupAtISO = at.toISOString();
            const orderUpdatePromise = updateOrder({
                ...order,
                customerName: editCustomerName.trim(),
                pickupAtISO: newPickupAtISO,
                pickupDate: derivePickupDate(newPickupAtISO),
                lines: updatedLines,
                note: editNote.trim() || undefined,
            });

            // Optimistic Update: We don't await the server confirmation to close the modal
            // In offline mode, awaiting would hang the UI.
            // We assume the local cache update is successful.

            // Handle success/fail in background
            orderUpdatePromise.then(async () => {
                const allPrepared = updatedLines.every((l) => l.preparedQuantity >= l.quantity);
                const somePrepared = updatedLines.some((l) => l.preparedQuantity < l.quantity);

                if (allPrepared && order.status === 'pending') {
                    await updateOrderStatus(order.id, 'ready');
                    // toast.success('Commande modifiée et marquée comme prête'); 
                } else if (somePrepared && order.status === 'ready') {
                    await updateOrderStatus(order.id, 'pending');
                    // toast.success('Commande modifiée et repassée en "À préparer"');
                } else {
                    // toast.success('Commande modifiée'); 
                }
            }).catch(e => {
                toast.error('Erreur de sauvegarde: ' + (e instanceof Error ? e.message : 'Inconnue'));
            });

            toast.success('Commande modifiée');
            onClose();
        } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Erreur lors de la modification');
        }
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Modifier la commande</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {(() => {
                        const editTotal = calculateOrderTotal(
                            editLines.map(l => ({
                                productId: l.productId,
                                quantity: l.quantity,
                                preparedQuantity: l.preparedQuantity,
                                options: l.options
                            })),
                            products
                        );
                        return editTotal !== null && (
                            <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-green-900">Total TTC</span>
                                    <span className="text-xl font-bold text-green-700">{editTotal.toFixed(2)} €</span>
                                </div>
                            </div>
                        );
                    })()}
                    <div className="space-y-2">
                        <Label>Nom du client</Label>
                        <Input
                            value={editCustomerName}
                            onChange={(e) => setEditCustomerName(e.target.value)}
                            placeholder="Nom du client"
                            className="h-11"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label>Date de récupération</Label>
                        <Input
                            type="date"
                            value={editPickupDate}
                            onChange={(e) => setEditPickupDate(e.target.value)}
                            className="h-11 w-full text-base"
                            style={{ fontSize: '16px' }}
                        />
                    </div>

                    <TimePicker
                        label="Heure de récupération"
                        value={editPickupTime}
                        onChange={setEditPickupTime}
                    />

                    <div className="space-y-2">
                        <Label>Note (optionnel)</Label>
                        <textarea
                            value={editNote}
                            onChange={(e) => setEditNote(e.target.value)}
                            placeholder="Ajouter une note personnalisée..."
                            className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="font-medium">Produits</div>
                            <span className="text-sm text-gray-500">{editLines.length} produit{editLines.length > 1 ? 's' : ''}</span>
                        </div>

                        {editLines.length === 0 ? (
                            <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                                <p className="text-gray-500 mb-4">Aucun produit ajouté</p>
                                <Button onClick={() => setProductModalOpen(true)}>
                                    Ajouter un produit
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-2">
                                    {editLines.map((line, index) => {
                                        const saleMode = getProductSaleMode(line.productId);
                                        const isWeight = saleMode === 'WEIGHT' || saleMode === 'WEIGHT_ESTIMATED';
                                        const increment = isWeight ? 0.1 : 1;

                                        return (
                                            <div key={index} className="rounded-md border bg-white p-3">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1">
                                                        <div className="font-medium mb-2">{line.productName}</div>

                                                        {/* Quantity controls */}
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleUpdateLineQuantity(index, line.quantity - increment)}
                                                                disabled={line.quantity === 0}
                                                                className="h-9 w-9 p-0 text-lg"
                                                            >
                                                                −
                                                            </Button>

                                                            <Popover open={editLinePopoverId === index} onOpenChange={(open) => setEditLinePopoverId(open ? index : null)}>
                                                                <PopoverTrigger asChild>
                                                                    <div
                                                                        className="text-lg font-bold min-w-[80px] text-center cursor-pointer hover:bg-gray-100 rounded px-2 py-1 select-none"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        {isWeight ? `${line.quantity.toFixed(3)} kg` : `${line.quantity}×`}
                                                                    </div>
                                                                </PopoverTrigger>
                                                                <PopoverContent className="w-auto p-0" align="center" sideOffset={5}>
                                                                    <NumericKeypad
                                                                        value={line.quantity}
                                                                        onValueChange={(val) => handleUpdateLineQuantity(index, val)}
                                                                        onConfirm={() => setEditLinePopoverId(null)}
                                                                        allowDecimals={isWeight}
                                                                    />
                                                                </PopoverContent>
                                                            </Popover>

                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => handleUpdateLineQuantity(index, line.quantity + increment)}
                                                                className="h-9 w-9 p-0 text-lg"
                                                            >
                                                                +
                                                            </Button>
                                                        </div>

                                                        {(() => {
                                                            const product = products.find(p => p.id === line.productId);
                                                            const optionLabels = product ? getOptionLabels(
                                                                { productId: line.productId, quantity: line.quantity, preparedQuantity: line.preparedQuantity, options: line.options },
                                                                product
                                                            ) : [];
                                                            return optionLabels.length > 0 && (
                                                                <div className="flex gap-2">
                                                                    {optionLabels.map((label, idx) => (
                                                                        <Badge key={idx} variant="secondary">{label}</Badge>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleRemoveLineFromEdit(index)}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-9 w-9"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <Button
                                    variant="outline"
                                    onClick={() => setProductModalOpen(true)}
                                    className="w-full"
                                >
                                    + Ajouter un produit
                                </Button>
                            </>
                        )}
                    </div>

                    <div className="flex gap-3 pt-4">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="flex-1"
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSaveOrder}
                            disabled={editLines.length === 0 || !editPickupTime}
                            className="flex-1"
                        >
                            Enregistrer
                        </Button>
                    </div>
                </div>
            </DialogContent>

            <ProductSelectionModal
                open={productModalOpen}
                onClose={() => setProductModalOpen(false)}
                onConfirm={(sel) => {
                    handleAddProductToEdit(sel);
                    setProductModalOpen(false);
                }}
                products={products}
                tenantSettings={tenantSettings}
            />
        </Dialog>
    );
}
